import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  UserCheck,
  RefreshCcw,
  Headphones,
  BriefcaseBusiness,
  Sparkles,
} from "lucide-react";

const items = [
  {
    title: "Verified Staff",
    desc: "Carefully screened support for your home.",
    icon: UserCheck,
    tone: "indigo",
  },
  {
    title: "Background Checked",
    desc: "A safer and more trusted hiring experience.",
    icon: ShieldCheck,
    tone: "sky",
  },
  {
    title: "Quick Replacement",
    desc: "Fast support when changes are needed.",
    icon: RefreshCcw,
    tone: "orange",
  },
  {
    title: "Support When Needed",
    desc: "Guidance and assistance throughout the process.",
    icon: Headphones,
    tone: "violet",
  },
  {
    title: "Flexible Hiring Options",
    desc: "Choose what suits your family and routine.",
    icon: BriefcaseBusiness,
    tone: "emerald",
  },
  {
    title: "Professional Experience",
    desc: "A smoother and more dependable service journey.",
    icon: Sparkles,
    tone: "pink",
  },
];

const toneStyles = {
  indigo: {
    iconWrap:
      "from-indigo-50 to-violet-50 border-indigo-100 text-indigo-700",
    topLine: "from-indigo-500 to-violet-500",
    glow: "from-indigo-100/50 via-transparent to-violet-100/40",
  },
  sky: {
    iconWrap: "from-sky-50 to-cyan-50 border-sky-100 text-sky-700",
    topLine: "from-sky-400 to-cyan-500",
    glow: "from-sky-100/50 via-transparent to-cyan-100/40",
  },
  orange: {
    iconWrap: "from-orange-50 to-amber-50 border-orange-100 text-orange-700",
    topLine: "from-orange-400 to-amber-500",
    glow: "from-orange-100/50 via-transparent to-amber-100/40",
  },
  violet: {
    iconWrap:
      "from-violet-50 to-purple-50 border-violet-100 text-violet-700",
    topLine: "from-violet-400 to-purple-500",
    glow: "from-violet-100/50 via-transparent to-purple-100/40",
  },
  emerald: {
    iconWrap:
      "from-emerald-50 to-teal-50 border-emerald-100 text-emerald-700",
    topLine: "from-emerald-400 to-teal-500",
    glow: "from-emerald-100/50 via-transparent to-teal-100/40",
  },
  pink: {
    iconWrap: "from-pink-50 to-rose-50 border-pink-100 text-pink-700",
    topLine: "from-pink-400 to-rose-500",
    glow: "from-pink-100/50 via-transparent to-rose-100/40",
  },
};

const HireWhyChooseUs = () => {
  return (
    <section className="relative px-4 py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#f8f5ff_0%,#f3efff_42%,#fdf8ff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_84%_18%,rgba(168,85,247,0.08),transparent_20%),radial-gradient(circle_at_80%_82%,rgba(59,130,246,0.06),transparent_22%)]" />
      <div className="absolute top-[-120px] right-[-80px] w-[260px] h-[260px] bg-purple-300/20 rounded-full blur-3xl" />
      <div className="absolute bottom-[-120px] left-[-80px] w-[280px] h-[280px] bg-indigo-300/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Trust, Safety & Convenience
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Why Families Choose A2Z
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            We focus on trust, convenience, and support so your hiring experience feels
            smooth and reliable from the very beginning.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
          {items.map((item, i) => {
            const Icon = item.icon;
            const tone = toneStyles[item.tone];

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="group relative overflow-hidden rounded-[28px] border border-white/70 bg-white/80 backdrop-blur-xl p-6 md:p-7 shadow-[0_14px_35px_rgba(15,23,42,0.07)] hover:shadow-[0_18px_45px_rgba(15,23,42,0.12)] transition-all duration-300 h-full flex flex-col"
              >
                {/* hover glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${tone.glow} pointer-events-none`}
                />

                {/* top accent */}
                <div
                  className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${tone.topLine}`}
                />

                {/* subtle corner glow */}
                <div className="absolute -top-3 -right-3 w-24 h-24 rounded-full bg-white/70 blur-2xl" />

                <div className="relative flex items-start gap-4 mb-5">
                  <div
                    className={`w-14 h-14 md:w-16 md:h-16 shrink-0 flex items-center justify-center rounded-2xl border bg-gradient-to-br ${tone.iconWrap} group-hover:scale-105 transition duration-300`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>

                  <div className="min-w-0 pt-1">
                    <h3 className="font-semibold text-gray-900 text-base md:text-xl leading-snug mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm md:text-[15px] text-gray-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>

                <div className="mt-auto pt-2">
                  <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HireWhyChooseUs;